/**
 * Appointment Scheduling Main JavaScript
 * Main coordinator for the appointment scheduling functionality
 *
 * IMPORTANT: When modifying this file, update APPOINTMENT_SCHEDULING_FEATURE.md
 * This file coordinates all appointment scheduling modules and manages global state
 */

// Global variables for pagination and state
let allScreenings = [];
let currentDisplayCount = 0;
let currentZipCode = '';
let currentSortBy = 'distance'; // Default sort by distance
const ITEMS_PER_PAGE = 15;

// Create global namespace for callbacks
window.appointmentScheduling = {
    currentSortBy: currentSortBy,
    onSortChange: handleSortChange,
    onZipCodeSearch: handleZipCodeSearch
};

// Initialize when DOM is ready
function initializeAppointmentScheduling() {

    // Try to get zip code from multiple sources in priority order:
    // 1. URL parameter (for shared links - takes precedence)
    // 2. Session storage (fallback for returning users)
    let zipCode = null;

    if (typeof getZipCodeFromURL === 'function') {
        zipCode = getZipCodeFromURL();
    }

    if (zipCode) {
        // Save URL zip code to session for consistency
        if (typeof saveZipCodeToSession === 'function') {
            saveZipCodeToSession(zipCode);
        }
    } else {
        if (typeof loadZipCodeFromSession === 'function') {
            zipCode = loadZipCodeFromSession();
            if (zipCode) {
                // Update URL to match session storage
                if (typeof updateURLWithZipCode === 'function') {
                    updateURLWithZipCode(zipCode);
                }
            }
        }
    }

    // If still no zip code, try manual session storage check as fallback
    if (!zipCode) {
        try {
            const savedZipCode = sessionStorage.getItem('validZipCode');
            const timestamp = sessionStorage.getItem('zipCodeTimestamp');

            if (savedZipCode && timestamp) {
                const oneHour = 60 * 60 * 1000;
                const now = Date.now();

                if (now - parseInt(timestamp) < oneHour) {
                    zipCode = savedZipCode;
                }
            }
        } catch (error) {
            // Silent error handling
        }
    }

    if (zipCode) {
        // Use the zip code for appointment scheduling functionality
        handleZipCodeAvailable(zipCode);

        // Check for saved appointment data after loading screenings
        setTimeout(() => {
            checkForSavedAppointmentData();
        }, 1000); // Wait for screenings to load
    } else {
        // Handle case where no zip code is available
        handleNoZipCode();
    }
}

// Callback handlers
function handleSortChange(sortType) {
    currentSortBy = sortType;
    allScreenings = sortScreenings(allScreenings, sortType);
    rerenderScreenings();
}

function handleZipCodeSearch(zipCode) {
    // Show skeleton loading and perform search
    handleZipCodeAvailable(zipCode);

    // Add a small delay to ensure skeleton is visible
    setTimeout(() => {
        performZipCodeSearch(zipCode);
    }, 100);
}

// Main workflow functions
function handleZipCodeAvailable(zipCode) {

    // Hide the zip code fallback and show appointment content
    const fallbackElement = document.getElementById('zip-code-fallback');
    const contentElement = document.getElementById('appointment-content');

    if (fallbackElement) {
        fallbackElement.style.display = 'none';
    }

    if (contentElement) {
        contentElement.style.display = 'grid';
        contentElement.innerHTML = `
            <div class="appointment-main">
                <h2 class="h2">Loading screenings near ${zipCode}...</h2>
                <div class="appointment-list">
                    ${generateSkeletonLoading()}
                </div>
            </div>
            <div class="appointment-map-container-desktop">
                <div class="skeleton-map">
                    <div class="skeleton-map-placeholder">
                        <div class="skeleton-map-text">Loading map...</div>
                    </div>
                </div>
            </div>
        `;
    }

    // Make API call to fetch screenings
    performZipCodeSearch(zipCode);
}

async function performZipCodeSearch(zipCode) {
    try {
        const data = await fetchScreeningsByZip(zipCode);

        // Store all screenings globally and reset pagination
        allScreenings = data;
        currentDisplayCount = 0;
        currentZipCode = zipCode;

        // Save zip code to session and update URL
        saveZipCodeToSession(zipCode);
        updateURLWithZipCode(zipCode);

        // Apply default sorting
        allScreenings = sortScreenings(allScreenings, currentSortBy);


        // Update the appointment content with initial batch
        updateAppointmentContent(zipCode, allScreenings, true);

    } catch (error) {
        // Update content to show error
        const contentElement = document.getElementById('appointment-content');
        if (contentElement) {
            contentElement.innerHTML = `
                <div class="appointment-error">
                    <h2 class="h2">Error Loading Screenings</h2>
                    <p class="text-medium">Unable to load screenings for ${zipCode}. Please try again later.</p>
                    <p class="appointment-error-message">Error: ${error.message}</p>
                </div>
            `;
        }
    }
}

function handleNoZipCode() {

    // Show the zip code fallback and hide appointment content
    const fallbackElement = document.getElementById('zip-code-fallback');
    const contentElement = document.getElementById('appointment-content');

    if (fallbackElement) {
        fallbackElement.style.display = 'block';
    }

    if (contentElement) {
        contentElement.style.display = 'none';
    }

    // Setup primary zip search functionality with a small delay to ensure DOM is ready
    setTimeout(() => {
        setupPrimaryZipSearch();
    }, 100);
}

// Content rendering and management functions
function updateAppointmentContent(zipCode, screenings, isInitialLoad = false) {
    const contentElement = document.getElementById('appointment-content');

    if (!contentElement) return;

    if (!screenings || screenings.length === 0) {
        renderNoScreeningsFound(zipCode);
        return;
    }

    if (isInitialLoad) {
        // Initial load - show first batch
        currentDisplayCount = Math.min(ITEMS_PER_PAGE, screenings.length);
        const displayScreenings = screenings.slice(0, currentDisplayCount);


        renderAppointmentContent(zipCode, displayScreenings, screenings);
    } else {
        // Load more - append to existing content
        loadMoreAppointments();
    }
}

function renderNoScreeningsFound(zipCode) {
    const contentElement = document.getElementById('appointment-content');
    contentElement.innerHTML = `
        <div class="appointment-main">
            <h2 class="h2">No Screenings Found near ${zipCode}</h2>
            <p class="text-medium">Please try a different location.</p>
            <!-- Secondary zip code search -->
            <div class="zip-code-search-container secondary-zip-container visible">
                <div class="zip-input-group">
                    <input type="text" class="zip-input-field" name="zipcode" id="secondary-zip-code-search" value="" inputmode="numeric" pattern="\\d{5}" maxlength="5" placeholder="Enter Zip Code" aria-label="Enter 5-digit zip code">
                    <button class="zip-code-button-search button-yellow" id="secondary-search-button">Change Zip Code</button>
                </div>
            </div>

        </div>
    `;

    // Setup secondary zip search functionality
    setupSecondaryZipSearch();
}

function renderAppointmentContent(zipCode, displayScreenings, allScreenings) {
    // Note: Gender selection should NOT be cleared here as it causes issues with accordion interactions

    const contentElement = document.getElementById('appointment-content');

    // Create HTML for screenings list
    const screeningsHtml = displayScreenings.map((screening, index) =>
        generateScreeningItemHTML(screening, index)
    ).join('');

    // Check if we need a "Load More" button
    const hasMore = currentDisplayCount < allScreenings.length;

    const loadMoreButton = hasMore ? createLoadMoreButtonHTML(allScreenings.length - currentDisplayCount) : '';

    // Update content
    contentElement.innerHTML = `
        <div class="appointment-main${window.hasRestrictedStates ? ' has-restricted-warning' : ''}">
            <h2 class="h2">Available Screenings near
            <span class="zip-code-display-inline" id="zip-code-display-inline">
                    <span class="zip-code-text">${zipCode}</span>
                    <button class="zip-code-edit-btn" id="zip-code-edit-btn" aria-label="Change zip code" title="Change zip code">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
                        </svg>
                        <span class="edit-text">edit</span>
                    </button>
                </span>
            </h2>

            <!-- Secondary zip code search -->
            <div class="zip-code-search-container secondary-zip-container">
                <div class="zip-input-group">
                    <input type="text" class="zip-input-field" name="zipcode" id="secondary-zip-code-search" value="" inputmode="numeric" pattern="\\d{5}" maxlength="5" placeholder="Enter Zip Code" aria-label="Enter 5-digit zip code">
                    <button class="zip-code-button-search button-yellow" id="secondary-search-button">Change Zip Code</button>
                </div>
            </div>


            <div class="appointment-controls">
                <div class="appointment-controls-inner">
                <div class="sort-dropdown" id="sort-dropdown">
                    <button class="sort-dropdown-trigger" id="sort-dropdown-trigger" type="button" aria-haspopup="listbox" aria-expanded="false" aria-labelledby="sort-label sort-selected">
                        <span class="sort-label" id="sort-label">Sort by:</span>
                        <span class="sort-selected" id="sort-selected">Distance</span>
                        <span class="dropdown-arrow" aria-hidden="true">▼</span>
                    </button>
                    <ul class="sort-dropdown-menu" id="sort-dropdown-menu" role="listbox" aria-labelledby="sort-label">
                        <li class="sort-option active" data-sort="distance" role="option" aria-selected="true" tabindex="0">Distance</li>
                        <li class="sort-option" data-sort="date" role="option" aria-selected="false" tabindex="-1">Date</li>
                    </ul>
                </div>
                <p class="appointment-count-text text-medium">
                Showing ${currentDisplayCount} of ${allScreenings.length} location${allScreenings.length !== 1 ? 's' : ''}.
            </p>
                </div>
                <button class="map-toggle-button" id="map-toggle-button">
                    <span class="map-toggle-text">See Map</span>
                </button>
            </div>
            <div class="appointment-map-container" id="appointment-map-container">
                <div id="appointment-map" style="height: 100%; width: 100%;"></div>
            </div>
            <div id="appointment-list">
                ${screeningsHtml}
            </div>
        </div>
        <div class="appointment-map-container-desktop" id="appointment-map-container-desktop">
            <div id="appointment-map-desktop" style="height: 100%; width: 100%;"></div>
        </div>
    `;

    // Add load more button if needed
    if (hasMore) {
        const appointmentList = document.getElementById('appointment-list');
        if (appointmentList) {
            const loadMoreHtml = createLoadMoreButtonHTML(allScreenings.length - currentDisplayCount);
            appointmentList.insertAdjacentHTML('afterend', loadMoreHtml);

            const loadMoreBtn = document.getElementById('load-more-btn');
            if (loadMoreBtn) {
                loadMoreBtn.addEventListener('click', loadMoreAppointments);
            }
        }
    }

    // Setup map toggle functionality
    const mapToggleButton = document.getElementById('map-toggle-button');
    if (mapToggleButton) {
        mapToggleButton.addEventListener('click', function() {
            const mapContainer = document.getElementById('appointment-map-container');
            const toggleText = mapToggleButton.querySelector('.map-toggle-text');

            if (mapContainer && toggleText) {
                if (mapContainer.classList.contains('mobile-map-visible')) {
                    // Hide map
                    mapContainer.classList.remove('mobile-map-visible');
                    toggleText.textContent = 'See Map';
                } else {
                    // Show map
                    mapContainer.classList.add('mobile-map-visible');
                    toggleText.textContent = 'Hide Map';

                    // Force map initialization/resize after showing
                    setTimeout(() => {
                        const currentScreenings = allScreenings.slice(0, currentDisplayCount);
                        loadGoogleMapsForAppointments(currentScreenings, 'appointment-map');
                    }, 100);
                }
            }
        });
    }

    // Setup accessible dropdown with keyboard navigation
    setupAccessibleSortDropdown();

    // Setup secondary zip search functionality
    setupSecondaryZipSearch();

    // Setup inline zip code edit functionality
    setupInlineZipCodeEdit();

    // Load Google Maps with currently displayed screenings
    updateMapsWithScreenings(displayScreenings);
}

function loadMoreAppointments() {
    const newDisplayCount = Math.min(currentDisplayCount + ITEMS_PER_PAGE, allScreenings.length);
    const newScreenings = allScreenings.slice(currentDisplayCount, newDisplayCount);

    // Update the display count
    currentDisplayCount = newDisplayCount;

    // Append new screenings to the list
    const appointmentList = document.getElementById('appointment-list');
    if (appointmentList) {
        const newScreeningsHtml = newScreenings.map((screening, index) =>
            generateScreeningItemHTML(screening, currentDisplayCount - newScreenings.length + index)
        ).join('');

        appointmentList.insertAdjacentHTML('beforeend', newScreeningsHtml);
    }

    // Update the counter and load more button
    const appointmentCountText = document.querySelector('.appointment-count-text');
    if (appointmentCountText) {
        appointmentCountText.innerHTML = `
            Showing ${currentDisplayCount} of ${allScreenings.length} location${allScreenings.length !== 1 ? 's' : ''} near
            <span class="zip-code-display-inline" id="zip-code-display-inline">
                <span class="zip-code-text">${currentZipCode}</span>
                <button class="zip-code-edit-btn" id="zip-code-edit-btn" aria-label="Change zip code" title="Change zip code">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
                    </svg>
                    <span class="edit-text">edit</span>
                </button>
            </span>.
        `;

        // Re-setup the edit functionality after updating the HTML
        setupInlineZipCodeEdit();
    }

    // Clean up existing load more buttons and add new one if needed
    const existingLoadMore = document.querySelectorAll('.load-more-container');
    existingLoadMore.forEach(container => container.remove());

    if (currentDisplayCount < allScreenings.length) {
        const appointmentList = document.getElementById('appointment-list');
        if (appointmentList) {
            const loadMoreHtml = createLoadMoreButtonHTML(allScreenings.length - currentDisplayCount);
            appointmentList.insertAdjacentHTML('afterend', loadMoreHtml);

            const loadMoreBtn = document.getElementById('load-more-btn');
            if (loadMoreBtn) {
                loadMoreBtn.addEventListener('click', loadMoreAppointments);
            }
        }
    }

    // Update both mobile and desktop maps with all currently displayed screenings
    const displayedScreenings = allScreenings.slice(0, currentDisplayCount);
    updateMapsWithScreenings(displayedScreenings);
}

function rerenderScreenings() {
    // Reset display count and re-render
    currentDisplayCount = Math.min(ITEMS_PER_PAGE, allScreenings.length);
    const displayScreenings = allScreenings.slice(0, currentDisplayCount);

    // Update the appointment list content
    const appointmentList = document.getElementById('appointment-list');
    if (appointmentList) {
        const screeningsHtml = displayScreenings.map((screening, index) =>
            generateScreeningItemHTML(screening, index)
        ).join('');

        appointmentList.innerHTML = screeningsHtml;
    }

    // Update the counter
    const textMedium = document.querySelector('.appointment-main .text-medium');
    if (textMedium) {
        textMedium.textContent = `Showing ${currentDisplayCount} of ${allScreenings.length} location${allScreenings.length !== 1 ? 's' : ''} near ${currentZipCode}.`;
    }

    // Update or add load more button
    updateLoadMoreButton();

    // Update maps with newly sorted screenings
    const displayedScreenings = allScreenings.slice(0, currentDisplayCount);
    updateMapsWithScreenings(displayedScreenings);

    // Re-setup inline zip code edit after re-render
    setupInlineZipCodeEdit();
}

function updateLoadMoreButton() {
    // Clean up all existing load more buttons first
    const existingLoadMore = document.querySelectorAll('.load-more-container');
    existingLoadMore.forEach(container => container.remove());

    const hasMore = currentDisplayCount < allScreenings.length;

    if (hasMore) {
        // Create new load more button
        const appointmentList = document.getElementById('appointment-list');
        if (appointmentList) {
            const loadMoreHtml = createLoadMoreButtonHTML(allScreenings.length - currentDisplayCount);
            appointmentList.insertAdjacentHTML('afterend', loadMoreHtml);

            const loadMoreBtn = document.getElementById('load-more-btn');
            if (loadMoreBtn) {
                loadMoreBtn.addEventListener('click', loadMoreAppointments);
            }
        }
    }
}

// Handle screening item clicks and show more appointments functionality
function initializeScreeningInteractions() {
    // Add keyboard event listener for appointment time slots
    document.addEventListener('keydown', function (e) {
        // Handle Enter key on appointment time slots
        if (e.key === 'Enter' && e.target.closest('.appointment-time-slot')) {
            e.preventDefault();
            e.stopPropagation();

            const timeSlot = e.target.closest('.appointment-time-slot');

            // Clear gender selection when selecting an appointment
            if (typeof window.AppointmentPackages?.clearGenderSelection === 'function') {
                window.AppointmentPackages.clearGenderSelection();
            }

            const screeningItem = timeSlot.closest('.screening-item');

            if (screeningItem) {
                let screening = null;

                // Get screening using the data-screening-id attribute
                const timeSlots = timeSlot.closest('.appointment-time-slots');
                if (timeSlots) {
                    const screeningId = timeSlots.getAttribute('data-screening-id');

                    // First try to find by actual screeningId
                    screening = allScreenings.find(s => s.screeningId && s.screeningId.toString() === screeningId);

                    // If not found, try to find by index (fallback for screenings without IDs)
                    if (!screening) {
                        const screeningIndex = parseInt(screeningId);
                        if (!isNaN(screeningIndex) && allScreenings[screeningIndex]) {
                            screening = allScreenings[screeningIndex];
                        }
                    }
                }

                if (screening) {
                    handleTimeSlotSelection(timeSlot, screening);
                }
            }
            return; // Exit early to prevent other handlers
        }

        // Handle Enter key on screening items (but not on appointment time slots)
        if (e.key === 'Enter' && e.target.closest('.screening-item') && !e.target.closest('.appointment-time-slot')) {
            e.preventDefault();

            const screeningItem = e.target.closest('.screening-item');

            // Try to get screening index from data attribute first (for detailed view)
            let screeningIndex = screeningItem.getAttribute('data-screening-index');

            if (screeningIndex !== null) {
                // Use the stored index from data attribute
                screeningIndex = parseInt(screeningIndex);
            } else {
                // Fallback to DOM position for list view
                screeningIndex = Array.from(document.querySelectorAll('.screening-item')).indexOf(screeningItem);
            }

            if (screeningIndex !== -1 && allScreenings[screeningIndex]) {
                showScreeningDetail(allScreenings[screeningIndex], screeningIndex);
            }
        }
    });

    document.addEventListener('click', function (e) {
        // Handle appointment time slot clicks first (higher priority)
        if (e.target.closest('.appointment-time-slot')) {
            e.stopPropagation(); // Prevent screening item click

            // Clear gender selection when selecting an appointment
            if (typeof window.AppointmentPackages?.clearGenderSelection === 'function') {
                window.AppointmentPackages.clearGenderSelection();
            }

            const timeSlot = e.target.closest('.appointment-time-slot');
            const screeningItem = timeSlot.closest('.screening-item');

            if (screeningItem) {
                let screening = null;

                // Get screening using the data-screening-id attribute
                const timeSlots = timeSlot.closest('.appointment-time-slots');
                if (timeSlots) {
                    const screeningId = timeSlots.getAttribute('data-screening-id');

                    // First try to find by actual screeningId
                    screening = allScreenings.find(s => s.screeningId && s.screeningId.toString() === screeningId);

                    // If not found, try to find by index (fallback for screenings without IDs)
                    if (!screening) {
                        const screeningIndex = parseInt(screeningId);
                        if (!isNaN(screeningIndex) && allScreenings[screeningIndex]) {
                            screening = allScreenings[screeningIndex];
                        }
                    }
                }

                if (screening) {
                    handleTimeSlotSelection(timeSlot, screening);
                }
            }
            return; // Exit early to prevent other handlers
        }

        // Handle screening item clicks (but not on buttons or time slots)
        if (e.target.closest('.screening-item') &&
            !e.target.closest('.show-more-appointments') &&
            !e.target.closest('.appointment-time-slot') &&
            !e.target.closest('.back-to-list-btn')) {

            const screeningItem = e.target.closest('.screening-item');

            // Try to get screening index from data attribute first (for detailed view)
            let screeningIndex = screeningItem.getAttribute('data-screening-index');

            if (screeningIndex !== null) {
                // Use the stored index from data attribute
                screeningIndex = parseInt(screeningIndex);
            } else {
                // Fallback to DOM position for list view
                screeningIndex = Array.from(document.querySelectorAll('.screening-item')).indexOf(screeningItem);
            }

            if (screeningIndex !== -1 && allScreenings[screeningIndex]) {
                showScreeningDetail(allScreenings[screeningIndex], screeningIndex);
            }
        }

        // Handle show more appointments functionality
        if (e.target.closest('.show-more-appointments')) {
            const button = e.target.closest('.show-more-appointments');
            const screeningId = button.getAttribute('data-screening-id');
            const timeSlots = document.querySelector(`[data-screening-id="${screeningId}"].appointment-time-slots`);
            const textSpan = button.querySelector('.text');

            if (timeSlots && textSpan) {
                if (timeSlots.classList.contains('collapsed')) {
                    // Expand - show all appointments
                    timeSlots.classList.remove('collapsed');
                    textSpan.textContent = 'Show Less';
                    button.classList.add('expanded');
                } else {
                    // Collapse - show only first 5 appointments
                    timeSlots.classList.add('collapsed');
                    textSpan.textContent = 'Show More';
                    button.classList.remove('expanded');
                }
            }
        }

        // Handle back to list button
        if (e.target.closest('.back-to-list-btn')) {
            showFullScreeningList();
        }

        // Handle back to appointments button (from package selection)
        if (e.target.closest('.back-to-appointments-btn')) {
            showAppointmentSelection();
        }
    });
}

// Show detailed view of a single screening
function showScreeningDetail(screening, screeningIndex) {
    const appointmentList = document.getElementById('appointment-list');
    if (!appointmentList) return;

    const date = screening.screeningDate ? screening.screeningDate.split("T")[0] : 'Date TBD';
    const appointmentCount = screening.appointments ? screening.appointments.length : 0;

    // Create detailed view HTML
    const detailHtml = `
        <div class="screening-detail-view">
            <div class="screening-detail-header">
                <button class="back-to-list-btn">
                    <span class="back-arrow">←</span>
                    <span class="back-text">Back to all locations</span>
                </button>
            </div>

            <div class="screening-item screening-item-detailed" data-screening-index="${screeningIndex}" data-screening-id="${screening.screeningId || screeningIndex}" tabindex="0" role="button" aria-label="View details for ${screening.siteName || 'Unknown Location'} screening on ${formatDate(date)}">
                <div class="screening-badges">
                    <div class="screening-distance-badge">${screening.distanceAway || 'N/A'} miles</div>
                </div>
                <div class="screening-date-text">${formatDate(date)}</div>
                <h3 class="screening-site-name">${screening.siteName || 'Unknown Location'}</h3>
                <p class="screening-address">${screening.address1 || ''} ${screening.address2 || ''}  ${screening.city || ''}, ${screening.state || ''} ${screening.zipCode || ''}</p>

                ${appointmentCount > 0 ? `
                    <div class="appointment-times">
                        <strong>Select a time:</strong>
                        <div class="appointment-time-slots expanded" data-screening-id="${screening.screeningId || screeningIndex}">
                            ${screening.appointments.map(apt =>
                                `<button class="appointment-time-slot" type="button" role="button" aria-label="Select appointment time ${apt.appointmentTime ? formatTime(apt.appointmentTime) : 'Time TBD'}" tabindex="0">
                                    ${apt.appointmentTime ? formatTime(apt.appointmentTime) : 'Time TBD'}
                                </button>`
                            ).join('')}
                        </div>
                    </div>
                ` : `
                    <div class="no-appointments">
                        <p>No appointment times available for this location.</p>
                    </div>
                `}
            </div>
        </div>
    `;

    // Update the appointment list
    appointmentList.innerHTML = detailHtml;

    // Hide UI elements that are not relevant for detailed view
    const elementsToHide = [
        '.appointment-count-text',
        '.appointment-controls',                   // Sort dropdown and map toggle
        '.secondary-zip-container'                 // Secondary zip code search
    ];

    elementsToHide.forEach(selector => {
        const element = document.querySelector(selector);
        if (element) {
            element.style.display = 'none';
        }
    });

    // Hide load more button specifically (it might be outside the main container)
    const loadMoreContainers = document.querySelectorAll('.load-more-container');
    loadMoreContainers.forEach(container => {
        container.style.display = 'none';
    });

    // Update map to show only this screening
    updateMapsWithScreenings([screening]);
}

// Show full screening list
function showFullScreeningList() {
    // Restore the full list view without resetting pagination
    const appointmentList = document.getElementById('appointment-list');
    if (!appointmentList) return;

    // Get the currently displayed screenings (maintain pagination)
    const displayScreenings = allScreenings.slice(0, currentDisplayCount);

    // Generate the HTML for all currently displayed screenings
    const screeningsHtml = displayScreenings.map((screening, index) =>
        generateScreeningItemHTML(screening, index)
    ).join('');

    // Update the appointment list
    appointmentList.innerHTML = screeningsHtml;

    // Clean up any existing load more buttons first
    const existingLoadMore = document.querySelectorAll('.load-more-container');
    existingLoadMore.forEach(container => container.remove());

    // Restore load more button if needed
    const hasMore = currentDisplayCount < allScreenings.length;
    if (hasMore) {
        const loadMoreHtml = createLoadMoreButtonHTML(allScreenings.length - currentDisplayCount);
        appointmentList.insertAdjacentHTML('afterend', loadMoreHtml);

        // Add event listener to the new load more button
        const loadMoreBtn = document.getElementById('load-more-btn');
        if (loadMoreBtn) {
            loadMoreBtn.addEventListener('click', loadMoreAppointments);
        }
    }

    // Restore UI elements that were hidden in detailed view
    const elementsToRestore = [
        '.appointment-count-text',
        '.appointment-controls',                   // Sort dropdown and map toggle
        '.secondary-zip-container'                 // Secondary zip code search
    ];

    elementsToRestore.forEach(selector => {
        const element = document.querySelector(selector);
        if (element) {
            element.style.display = '';  // Reset to default display
        }
    });

    // Update maps with all currently displayed screenings
    const displayedScreenings = allScreenings.slice(0, currentDisplayCount);
    updateMapsWithScreenings(displayedScreenings);
}

// Setup inline zip code edit functionality
function setupInlineZipCodeEdit() {
    const editBtn = document.getElementById('zip-code-edit-btn');
    const secondaryContainer = document.querySelector('.secondary-zip-container');

    if (editBtn && secondaryContainer) {
        editBtn.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();

            // Toggle the secondary zip container
            if (secondaryContainer.classList.contains('visible')) {
                // Hide the container
                secondaryContainer.classList.remove('visible');
                editBtn.setAttribute('aria-label', 'Change zip code');
                editBtn.setAttribute('title', 'Change zip code');
            } else {
                // Show the container
                secondaryContainer.classList.add('visible');
                editBtn.setAttribute('aria-label', 'Hide zip code input');
                editBtn.setAttribute('title', 'Hide zip code input');

                // Focus the zip code input after a short delay for animation
                setTimeout(() => {
                    const zipInput = document.querySelector('#secondary-zip-code-search');
                    const zipDisplay = document.querySelector('.secondary-zip-container .zip-code-display');
                    if (zipDisplay) {
                        zipDisplay.focus();
                    } else if (zipInput) {
                        zipInput.focus();
                    }
                }, 100);
            }
        });

        // Close with Escape key
        document.addEventListener('keydown', function(e) {
            if (e.key === 'Escape' && secondaryContainer.classList.contains('visible')) {
                secondaryContainer.classList.remove('visible');
                editBtn.setAttribute('aria-label', 'Change zip code');
                editBtn.setAttribute('title', 'Change zip code');
                editBtn.focus(); // Return focus to the edit button
            }
        });
    }
}

// Auto-initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
    initializeAppointmentScheduling();
    addLoadMoreStyles();
    initializeScreeningInteractions();
});

// Add CSS styles for load more functionality
function addLoadMoreStyles() {
    if (document.getElementById('appointment-load-more-styles')) return;

    const style = document.createElement('style');
    style.id = 'appointment-load-more-styles';
    style.textContent = `
        .load-more-container {
            text-align: center;
            margin: 2rem 0;
            padding: 1rem;
        }

        .load-more-btn {
            background: var(--color-bright-yellow);
            color: var(--color-navy-blue);
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0);
            border: 4px solid var(--color-bright-yellow);
            padding: 20px;
            border-radius: 999px;
            font-size: 20px;
            font-weight: 700;
            cursor: pointer;
            transition: all 0.4s ease;
            display: flex;
            justify-content: center;
            align-items: center;
            white-space: nowrap;
            width: fit-content;
            position: relative;
            overflow: hidden;
            z-index: 1;
            margin: 0 auto;
        }

        .load-more-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
        }

        .load-more-btn:focus-visible {
            outline: 4px solid var(--color-bright-yellow);
        }

        .load-more-btn:disabled {
            background: #6c757d;
            border-color: #6c757d;
            cursor: not-allowed;
            transform: none;
            box-shadow: none;
        }

        @media (max-width: 900px) {
            .load-more-btn {
                padding: 13px 50px;
                font-size: 16px;
            }
        }
    `;
    document.head.appendChild(style);
}
